<template>
  <div class="sound-creature" :class="`state-${state}`" role="img" :aria-label="label">
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path class="sound-body" :d="bodyPath" />
      <g class="sound-face">
        <g class="sound-eyes">
          <rect x="23" y="23" width="4" height="9" rx="2" />
          <rect x="36" y="23" width="4" height="9" rx="2" />
        </g>
        <path class="sound-mouth" :d="inviting ? 'M29 41 C29 37 35 37 35 41 C35 45 29 45 29 41Z' : 'M29 41 Q32 43 35 41'" />
      </g>
      <g class="sound-wave">
        <rect v-for="(height, i) in waveHeights" :key="i" :x="18 + i * 6" :y="32 - height / 2" width="4" :height="height" rx="2" />
      </g>
      <path class="listening-mark" d="M6 25 Q2 32 6 39" />
    </svg>
  </div>
</template>

<script>
export default {
  name: 'SoundCreature',
  props: {
    state: { type: String, default: 'idle' },
    label: { type: String, required: true },
    level: { type: Number, default: 0 },
    bars: { type: Array, default: () => [] },
  },
  computed: {
    inviting() { return this.state === 'ready' || this.state === 'waiting' },
    energy() {
      return this.state === 'speaking' && Number.isFinite(this.level)
        ? Math.sqrt(Math.max(0, Math.min(1, this.level))) : 0
    },
    bodyPath() {
      const e = this.energy
      const w = 22 + e * 4
      const h = 23 - e * 2
      // Symmetric ellipse with tangent-matched curves; audio controls its proportions.
      const k = 0.55228475
      return `M32 ${32-h} C${32+w*k} ${32-h} ${32+w} ${32-h*k} ${32+w} 32 C${32+w} ${32+h*k} ${32+w*k} ${32+h} 32 ${32+h} C${32-w*k} ${32+h} ${32-w} ${32+h*k} ${32-w} 32 C${32-w} ${32-h*k} ${32-w*k} ${32-h} 32 ${32-h}Z`
    },
    waveHeights() {
      return Array.from({ length: 5 }, (_, i) => {
        const value = Math.max(this.bars[i * 2] || 0, this.bars[i * 2 + 1] || 0)
        return this.state === 'speaking' ? Math.max(3, Math.pow(Math.min(1, value), 0.72) * 32) : 3
      })
    },
  },
}
</script>

<style scoped>
.sound-creature {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  color: var(--signal-active-strong);
}
.sound-creature svg { display: block; width: 100%; height: 100%; overflow: visible; }
.sound-body {
  fill: currentColor;
  transition: d 100ms ease-out;
  transform-origin: 32px 32px;
}
.sound-face { fill: var(--surface-panel); transition: opacity 200ms ease; }
.sound-eyes { transform-origin: 32px 27px; animation: sound-blink 5.8s infinite; }
.sound-mouth { fill: none; stroke: var(--surface-panel); stroke-width: 1.6; stroke-linecap: round; }
.sound-wave { fill: var(--surface-panel); opacity: 0; transition: opacity 180ms ease; }
.sound-wave rect { transition: none; }
.listening-mark { fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; opacity: 0; }
.state-idle .sound-face,
.state-listening .sound-face { transform-origin: 32px 32px; animation: sound-look 2.4s ease-in-out infinite; }
.state-listening .sound-body { animation: sound-attend 2.4s ease-in-out infinite; }
.state-listening .listening-mark { opacity: 0.5; animation: sound-hear 1.8s ease-in-out infinite; }
.state-ready .sound-face,
.state-waiting .sound-face { transform: none; }
.state-ready .sound-mouth,
.state-waiting .sound-mouth { fill: var(--surface-panel); stroke: none; transform-origin: 32px 41px; animation: sound-speak 1.4s ease-in-out infinite; }
.state-ready .sound-body,
.state-waiting .sound-body { animation: sound-invite 2.8s ease-in-out infinite; }
.state-speaking .sound-face { opacity: 0; }
.state-speaking .sound-wave { opacity: 1; }
.state-paused { color: var(--ink-tertiary); }
.state-paused .sound-eyes { animation: none; transform: scaleY(0.18); }
.state-paused .sound-mouth { opacity: 0.72; }
@keyframes sound-blink { 0%, 43%, 47%, 100% { transform: scaleY(1); } 45% { transform: scaleY(0.12); } }
@keyframes sound-attend { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-1px); } }
@keyframes sound-look { 0%, 100% { transform: translate(0, 0); } 35%, 65% { transform: translate(-4px, -2px); } }
@keyframes sound-speak { 0%, 100% { transform: scale(1.15, 0.25); } 30% { transform: scale(1.2, 1.5); } 58% { transform: scale(0.9, 0.65); } 78% { transform: scale(1.1, 1.25); } }
@keyframes sound-hear { 0%, 100% { opacity: 0.25; } 50% { opacity: 0.65; } }
@keyframes sound-invite { 0%, 100% { transform: translateY(0) scale(1); } 45% { transform: translateY(-1px) scale(0.97, 1.03); } }
@media (prefers-reduced-motion: reduce) {
  .sound-body, .sound-eyes, .sound-face, .sound-mouth, .listening-mark { animation: none; }
  .sound-body, .sound-face, .sound-wave, .sound-wave rect { transition: none; }
}
</style>
